import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { products } from '../data/products'
import { renderHighlighted } from '../utils/renderHighlighted'

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeId, setActiveId] = useState(id ?? products[0].id)

  useEffect(() => {
    if (id) setActiveId(id)
  }, [id])

  const active = products.find((p) => p.id === activeId) ?? products[0]
  const Icon = active.icon

  const handleTab = (productId: string) => {
    setActiveId(productId)
    navigate(`/products/${productId}`, { replace: true })
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero band */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-950 via-indigo-800 to-indigo-600 px-6 pb-16 pt-8 lg:px-8">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(99,102,241,0.3)_0%,_transparent_60%)]" />
        <div className="mx-auto max-w-6xl">
          <button
            onClick={() => navigate('/')}
            className="mb-10 inline-flex items-center gap-1.5 text-sm text-indigo-300 transition-colors hover:text-white"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Home
          </button>
          <div className="flex items-center gap-5">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/20 backdrop-blur-sm">
              <Icon className="h-8 w-8 text-white" />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-indigo-300">
                Product
              </p>
              <h1 className="text-3xl font-bold text-white sm:text-4xl">{active.name}</h1>
              <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-indigo-200">
                {active.tagline}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        {/* Mobile tab strip — scrollable */}
        <div className="-mx-6 overflow-x-auto px-6 py-5 lg:hidden">
          <div className="flex gap-2 whitespace-nowrap">
            {products.map((p) => (
              <button
                key={p.id}
                onClick={() => handleTab(p.id)}
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-all ${
                  activeId === p.id
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
                    : 'border border-gray-200 bg-white text-gray-600 hover:border-indigo-300 hover:text-indigo-600'
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>

        {/* Desktop: sidebar + content */}
        <div className="flex gap-10 pb-24 lg:pt-10">
          {/* Sidebar */}
          <aside className="hidden lg:block w-52 shrink-0">
            <nav className="sticky top-8 space-y-1">
              {products.map((p) => {
                const PIcon = p.icon
                return (
                  <button
                    key={p.id}
                    onClick={() => handleTab(p.id)}
                    className={`group flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-medium transition-all ${
                      activeId === p.id
                        ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200/60'
                        : 'text-gray-500 hover:bg-white hover:text-indigo-600 hover:shadow-sm'
                    }`}
                  >
                    <PIcon
                      className={`h-4 w-4 shrink-0 ${
                        activeId === p.id ? 'text-white' : 'text-gray-400 group-hover:text-indigo-500'
                      }`}
                    />
                    {p.name}
                  </button>
                )
              })}
            </nav>
          </aside>

          {/* Content */}
          <main className="min-w-0 flex-1 space-y-5">
            {/* Overview */}
            <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
              <div className="space-y-3">
                {active.description.map((para, i) => (
                  <p key={i} className="text-base leading-relaxed text-gray-600">
                    {renderHighlighted(para)}
                  </p>
                ))}
              </div>
            </div>

            {active.sections ? (
              <>
                {/* Our Services */}
                {active.sections.services && (
                  <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
                    <h2 className="mb-6 text-lg font-bold text-gray-900">Our Services</h2>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {active.sections.services.map((item) => (
                        <div
                          key={item}
                          className="flex items-start gap-3 rounded-xl bg-indigo-50/60 px-4 py-3.5 ring-1 ring-indigo-100"
                        >
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                          <span className="text-sm leading-snug text-gray-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Supported Payment Schemes */}
                {active.sections.schemes && (
                  <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
                    <h2 className="mb-6 text-lg font-bold text-gray-900">
                      Supported Payment Schemes
                    </h2>
                    <div className="flex flex-wrap gap-2.5">
                      {active.sections.schemes.map((scheme) => (
                        <span
                          key={scheme}
                          className="rounded-full border border-indigo-200 bg-indigo-50 px-5 py-2 text-sm font-semibold text-indigo-700"
                        >
                          {scheme}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Supported Devices */}
                {active.sections.devices && (
                  <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
                    <h2 className="mb-6 text-lg font-bold text-gray-900">Supported Devices</h2>
                    <div className="flex flex-wrap gap-2.5">
                      {active.sections.devices.map((device) => (
                        <span
                          key={device}
                          className="rounded-full border border-indigo-200 bg-indigo-50 px-5 py-2 text-sm font-semibold text-indigo-700"
                        >
                          {device}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Key Features */}
                {active.sections.keyFeatures && (
                  <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
                    <h2 className="mb-6 text-lg font-bold text-gray-900">Key Features</h2>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {active.sections.keyFeatures.map((feature) => (
                        <div
                          key={feature}
                          className="rounded-xl bg-slate-50 px-4 py-4 ring-1 ring-gray-100"
                        >
                          <div className="mb-2.5 h-1 w-8 rounded-full bg-indigo-500" />
                          <span className="text-sm leading-snug text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Key Capabilities */}
                {active.sections.capabilities && (
                  <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
                    <h2 className="mb-6 text-lg font-bold text-gray-900">Key Capabilities</h2>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {active.sections.capabilities.map((item) => (
                        <div
                          key={item}
                          className="rounded-xl bg-slate-50 px-4 py-4 ring-1 ring-gray-100"
                        >
                          <div className="mb-2.5 h-1 w-8 rounded-full bg-indigo-500" />
                          <span className="text-sm leading-snug text-gray-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Supported Areas */}
                {active.sections.supportedAreas && (
                  <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
                    <h2 className="mb-6 text-lg font-bold text-gray-900">Supported Areas</h2>
                    <div className="flex flex-wrap gap-2.5">
                      {active.sections.supportedAreas.map((area) => (
                        <span
                          key={area}
                          className="rounded-full border border-indigo-200 bg-indigo-50 px-5 py-2 text-sm font-semibold text-indigo-700"
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Benefits */}
                {active.sections.benefits && (
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-800 to-indigo-950 p-8 shadow-sm">
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(165,180,252,0.15)_0%,_transparent_60%)]" />
                    <h2 className="mb-6 text-lg font-bold text-white">Benefits</h2>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {active.sections.benefits.map((benefit) => (
                        <div
                          key={benefit}
                          className="flex items-start gap-3 rounded-xl bg-white/8 px-4 py-3.5 ring-1 ring-white/10 backdrop-blur-sm"
                        >
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-300" />
                          <span className="text-sm leading-snug text-indigo-100">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
                <h3 className="mb-5 text-base font-bold text-gray-900">Key Features</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {active.bullets.map((bullet) => (
                    <div
                      key={bullet}
                      className="flex items-start gap-3 rounded-xl bg-indigo-50/60 px-4 py-3.5 ring-1 ring-indigo-100"
                    >
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                      <span className="text-sm leading-snug text-gray-700">{bullet}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  )
}
